import { useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";

const REASONS = [
  { value: "wrong_company",   label: "Wrong Company" },
  { value: "equipment_sold",  label: "Equipment Sold" },
  { value: "other",           label: "Other" },
];

// Detects existing per-line allocations that would be overwritten by a
// whole-invoice reject. Mirrors the backend rule (reject reallocates every
// non-empty correction to 100% rejected).
const OVERWRITABLE_BUCKETS = new Set(["driver", "lessee", "warranty", "duplicate", "company"]);

export default function RejectInvoiceDialog({
  open, onOpenChange, invoice, onSuccess,
}) {
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const trimmedComment = comment.trim();
  const canSubmit = !!reason && trimmedComment.length > 0 && !submitting;

  // Warn (don't block) if there are existing non-rejected allocations that
  // will get overwritten by the whole-invoice reject.
  const overwriteWarning = useMemo(() => {
    if (!invoice?.corrections) return null;
    let count = 0;
    for (const c of invoice.corrections) {
      for (const a of (c.charge_allocations || [])) {
        if (OVERWRITABLE_BUCKETS.has(a.charge_type)) { count += 1; break; }
      }
    }
    return count > 0 ? count : null;
  }, [invoice]);

  const reset = () => {
    setReason(""); setComment(""); setInlineError(""); setSubmitting(false);
  };

  const handleClose = (v) => {
    if (submitting) return;
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setInlineError("");
    try {
      const { data } = await api.post(
        `/equipment-repairs/invoices/${invoice.id}/reject`,
        { reason, comment: trimmedComment },
      );
      // Success. Backend returns claim_id + rollup_status.
      const claimBits = data?.claim_id ? ` — Shop Credit Claim ${String(data.claim_id).slice(-6)} created` : "";
      toast.success(`Invoice rejected${claimBits}`);
      reset();
      onOpenChange(false);
      onSuccess?.(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (detail?.message || extractError(e));
      // Show 400 and 409 inline; other errors as toast so the user can retry.
      if (status === 400 || status === 409) {
        setInlineError(msg);
      } else {
        toast.error(msg || "Failed to reject invoice");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="reject-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Reject invoice {invoice?.invoice_number ? `#${invoice.invoice_number}` : ""}</DialogTitle>
          <DialogDescription>
            Every correction on this invoice will be reallocated to <strong>rejected</strong> and a Shop Credit
            claim will be auto-created against the shop.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {overwriteWarning && (
            <Alert
              className="border-amber-200 bg-amber-50 text-amber-800"
              data-testid="reject-invoice-overwrite-warning"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription>
                This invoice has existing allocations on {overwriteWarning}{" "}
                correction{overwriteWarning === 1 ? "" : "s"} that will be overwritten by the reject.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="reject-invoice-reason" className="text-xs font-medium uppercase tracking-wide text-slate-600">
              Reason
            </Label>
            <Select value={reason} onValueChange={setReason} disabled={submitting}>
              <SelectTrigger
                id="reject-invoice-reason"
                className="h-9"
                data-testid="reject-invoice-reason-trigger"
              >
                <SelectValue placeholder="Pick a reason…" />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem
                    key={r.value}
                    value={r.value}
                    data-testid={`reject-invoice-reason-${r.value}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reject-invoice-comment" className="text-xs font-medium uppercase tracking-wide text-slate-600">
              Comment <span className="text-rose-600">*</span>
            </Label>
            <Textarea
              id="reject-invoice-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder="Why is this invoice being rejected? (Required)"
              data-testid="reject-invoice-comment"
            />
            {comment.length > 0 && trimmedComment.length === 0 && (
              <div className="text-[11px] text-rose-600">Comment cannot be blank.</div>
            )}
          </div>

          {inlineError && (
            <Alert variant="destructive" data-testid="reject-invoice-error">
              <AlertDescription>{inlineError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={submitting}
            data-testid="reject-invoice-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
            data-testid="reject-invoice-confirm"
          >
            {submitting
              ? (<><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Rejecting…</>)
              : "Reject invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Small confirm dialog for POST /reject/reverse. Optional comment.
export function ReverseRejectDialog({
  open, onOpenChange, invoice, onSuccess,
}) {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (v) => {
    if (submitting) return;
    if (!v) { setComment(""); setSubmitting(false); }
    onOpenChange(v);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const body = comment.trim() ? { comment: comment.trim() } : {};
      const { data } = await api.post(
        `/equipment-repairs/invoices/${invoice.id}/reject/reverse`,
        body,
      );
      const voided = data?.voided_claims?.length || 0;
      toast.success(
        voided > 0
          ? `Reject reversed — ${voided} shop credit claim${voided === 1 ? "" : "s"} voided.`
          : "Reject reversed.",
      );
      setComment("");
      onOpenChange(false);
      onSuccess?.(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="reverse-reject-dialog">
        <DialogHeader>
          <DialogTitle>Reverse reject on invoice {invoice?.invoice_number ? `#${invoice.invoice_number}` : ""}?</DialogTitle>
          <DialogDescription>
            Corrections will be restored to their pre-reject state. Any draft or sent Shop Credit
            claim created by this reject will be voided.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="reverse-reject-comment" className="text-xs font-medium uppercase tracking-wide text-slate-600">
            Comment (optional)
          </Label>
          <Textarea
            id="reverse-reject-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={submitting}
            rows={2}
            placeholder="Optional note for the audit trail"
            data-testid="reverse-reject-comment"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={submitting}
            data-testid="reverse-reject-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90 disabled:opacity-60"
            data-testid="reverse-reject-confirm"
          >
            {submitting
              ? (<><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Reversing…</>)
              : "Reverse reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
